import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FileText, Receipt, Scale, BarChart3, Plus, Trash2, Eye, Download, X, FileSignature, Handshake, AlertTriangle, Briefcase, IdCard } from "lucide-react";
import { useClients, displayName } from "@/api/clients";
import { useInvoices, invoiceBalance, INVOICE_STATUS_LABEL } from "@/api/invoices";
import { useLegalCases, LEGAL_CASE_STATUS_LABEL } from "@/api/legal";
import { useEmployees, EMPLOYEE_STATUS_LABEL } from "@/api/employees";
import { downloadDocumentBytesGlobal } from "@/api/documents";
import { usePreviewGeneratedDocument, useGenerateDocument, DOC_TYPE_LABEL, DocGeneratorType, DocGeneratorPayload } from "@/api/documentGenerator";
import { getApiErrorMessage } from "@/api/client";

/**
 * Generador de Documentos: arma cuentas de cobro, facturas, expedientes
 * jurídicos e informes con el diseño membreteado navy/dorado, siempre a
 * partir de datos reales de la organización/cliente/factura/caso (nunca
 * datos de relleno). Reemplaza el antiguo botón de "carta de cobro" en
 * .docx plano.
 */

const TYPE_OPTIONS: Array<{ type: DocGeneratorType; icon: typeof FileText; hint: string }> = [
  { type: "CUENTA_COBRO", icon: FileText, hint: "Cobro de un saldo pendiente, con o sin factura asociada" },
  { type: "FACTURA", icon: Receipt, hint: "Factura de venta ya registrada en el sistema" },
  { type: "JURIDICO", icon: Scale, hint: "Reporte de estado de un expediente jurídico" },
  { type: "PODER_ESPECIAL", icon: FileSignature, hint: "Poder especial para un caso jurídico" },
  { type: "ACTA_CONCILIACION", icon: Handshake, hint: "Acta de conciliación entre las partes" },
  { type: "REQUERIMIENTO_PAGO", icon: AlertTriangle, hint: "Notificación formal previa de cobro, con plazo" },
  { type: "CONTRATO_SERVICIOS", icon: Briefcase, hint: "Contrato de prestación de servicios profesionales" },
  { type: "CERTIFICACION_LABORAL", icon: IdCard, hint: "Certificación laboral de un empleado de un cliente" },
  { type: "INFORME", icon: BarChart3, hint: "Informe empresarial con indicadores y análisis" },
];

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

interface Indicador {
  label: string;
  valor: string;
}

export default function DocumentGeneratorPage() {
  const [docType, setDocType] = useState<DocGeneratorType>("CUENTA_COBRO");

  // Cliente (usado para filtrar facturas/casos y para Cuenta de cobro manual / Informe)
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<{ id: string; name: string } | null>(null);
  const { data: clientResults } = useClients({ search: clientSearch, pageSize: 6 });

  // Cuenta de cobro
  const [cuentaMode, setCuentaMode] = useState<"invoice" | "manual">("invoice");
  const [concepto, setConcepto] = useState("");
  const [valor, setValor] = useState("");

  // Factura / Cuenta de cobro desde factura
  const [selectedInvoiceId, setSelectedInvoiceId] = useState("");

  // Jurídico
  const [selectedCaseId, setSelectedCaseId] = useState("");
  const [ultimaActuacion, setUltimaActuacion] = useState("");
  const [proximaActuacion, setProximaActuacion] = useState("");

  // Poder especial
  const [apoderadoNombre, setApoderadoNombre] = useState("");
  const [apoderadoTarjetaProfesional, setApoderadoTarjetaProfesional] = useState("");
  const [facultades, setFacultades] = useState(
    "Representar al poderdante en todas las etapas del proceso, presentar y contestar demandas, recursos e incidentes, conciliar, transigir, desistir, sustituir el poder y, en general, realizar todos los actos necesarios para la defensa de sus intereses."
  );

  // Acta de conciliación
  const [lugar, setLugar] = useState("");
  const [hechos, setHechos] = useState("");
  const [acuerdos, setAcuerdos] = useState("");
  const [compromisos, setCompromisos] = useState("");

  // Requerimiento de pago
  const [requerimientoMode, setRequerimientoMode] = useState<"invoice" | "manual">("invoice");
  const [reqConcepto, setReqConcepto] = useState("");
  const [reqValor, setReqValor] = useState("");
  const [plazoDias, setPlazoDias] = useState("5");
  const [advertencia, setAdvertencia] = useState("");

  // Contrato de servicios
  const [objeto, setObjeto] = useState("");
  const [valorHonorarios, setValorHonorarios] = useState("");
  const [formaPago, setFormaPago] = useState("");
  const [vigencia, setVigencia] = useState("");
  const [clausulasAdicionales, setClausulasAdicionales] = useState("");

  // Certificación laboral
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [dirigidoA, setDirigidoA] = useState("");
  const [proposito, setProposito] = useState("");

  // Informe
  const [periodo, setPeriodo] = useState("");
  const [resumen, setResumen] = useState("");
  const [indicadores, setIndicadores] = useState<Indicador[]>([{ label: "", valor: "" }]);
  const [analisis, setAnalisis] = useState("");
  const [hallazgos, setHallazgos] = useState("");
  const [recomendaciones, setRecomendaciones] = useState("");

  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const preview = usePreviewGeneratedDocument();
  const generate = useGenerateDocument();

  const needsClient =
    docType === "INFORME" ||
    (docType === "CUENTA_COBRO" && cuentaMode === "manual") ||
    docType === "FACTURA" ||
    docType === "JURIDICO" ||
    docType === "PODER_ESPECIAL" ||
    docType === "ACTA_CONCILIACION" ||
    (docType === "REQUERIMIENTO_PAGO" && requerimientoMode === "manual") ||
    docType === "CONTRATO_SERVICIOS" ||
    docType === "CERTIFICACION_LABORAL";

  const { data: clientInvoices } = useInvoices({ clientId: selectedClient?.id, pageSize: 50 }, { enabled: !!selectedClient?.id });
  const { data: clientCases } = useLegalCases({ clientId: selectedClient?.id, pageSize: 50 }, { enabled: !!selectedClient?.id });
  const { data: clientEmployees } = useEmployees({ clientId: selectedClient?.id, pageSize: 50 }, { enabled: !!selectedClient?.id });

  const invoiceOptions = useMemo(() => {
    const list = clientInvoices?.data ?? [];
    if (docType === "CUENTA_COBRO" || docType === "REQUERIMIENTO_PAGO") {
      return list.filter((inv) => !["BORRADOR", "ANULADA"].includes(inv.status) && invoiceBalance(inv) > 0);
    }
    return list.filter((inv) => inv.status !== "BORRADOR");
  }, [clientInvoices, docType]);

  const caseOptions = clientCases?.data ?? [];
  const employeeOptions = clientEmployees?.data ?? [];

  // Al cambiar de tipo o de cliente, limpiar selecciones dependientes.
  useEffect(() => {
    setSelectedInvoiceId("");
    setSelectedCaseId("");
    setSelectedEmployeeId("");
    setPreviewHtml(null);
  }, [docType, selectedClient?.id]);

  const buildPayload = (): DocGeneratorPayload | null => {
    if (docType === "CUENTA_COBRO") {
      if (cuentaMode === "invoice") {
        if (!selectedInvoiceId) return null;
        return { docType, invoiceId: selectedInvoiceId };
      }
      if (!selectedClient || !concepto.trim() || !valor || Number(valor) <= 0) return null;
      return { docType, clientId: selectedClient.id, concepto: concepto.trim(), valor: Number(valor) };
    }
    if (docType === "FACTURA") {
      if (!selectedInvoiceId) return null;
      return { docType, invoiceId: selectedInvoiceId };
    }
    if (docType === "JURIDICO") {
      if (!selectedCaseId || !ultimaActuacion.trim()) return null;
      return { docType, legalCaseId: selectedCaseId, ultimaActuacion: ultimaActuacion.trim(), proximaActuacion: proximaActuacion.trim() || undefined };
    }
    if (docType === "PODER_ESPECIAL") {
      if (!selectedCaseId || !apoderadoNombre.trim() || !facultades.trim()) return null;
      return {
        docType,
        legalCaseId: selectedCaseId,
        apoderadoNombre: apoderadoNombre.trim(),
        apoderadoTarjetaProfesional: apoderadoTarjetaProfesional.trim() || undefined,
        facultades: facultades.trim(),
      };
    }
    if (docType === "ACTA_CONCILIACION") {
      if (!selectedClient || !lugar.trim() || !acuerdos.trim()) return null;
      return {
        docType,
        clientId: selectedClient.id,
        legalCaseId: selectedCaseId || undefined,
        lugar: lugar.trim(),
        hechos: hechos.trim() || undefined,
        acuerdos: acuerdos.trim(),
        compromisos: compromisos.trim() || undefined,
      };
    }
    if (docType === "REQUERIMIENTO_PAGO") {
      const dias = Number(plazoDias);
      if (!dias || dias <= 0) return null;
      if (requerimientoMode === "invoice") {
        if (!selectedInvoiceId) return null;
        return { docType, invoiceId: selectedInvoiceId, plazoDias: dias, advertencia: advertencia.trim() || undefined };
      }
      if (!selectedClient || !reqConcepto.trim() || !reqValor || Number(reqValor) <= 0) return null;
      return {
        docType,
        clientId: selectedClient.id,
        concepto: reqConcepto.trim(),
        valor: Number(reqValor),
        plazoDias: dias,
        advertencia: advertencia.trim() || undefined,
      };
    }
    if (docType === "CONTRATO_SERVICIOS") {
      if (!selectedClient || !objeto.trim() || !valorHonorarios || Number(valorHonorarios) <= 0 || !formaPago.trim() || !vigencia.trim()) return null;
      return {
        docType,
        clientId: selectedClient.id,
        objeto: objeto.trim(),
        valorHonorarios: Number(valorHonorarios),
        formaPago: formaPago.trim(),
        vigencia: vigencia.trim(),
        clausulasAdicionales: clausulasAdicionales.trim() || undefined,
      };
    }
    if (docType === "CERTIFICACION_LABORAL") {
      if (!selectedEmployeeId) return null;
      return { docType, employeeId: selectedEmployeeId, dirigidoA: dirigidoA.trim() || undefined, proposito: proposito.trim() || undefined };
    }
    // INFORME
    if (!selectedClient || !periodo.trim() || !resumen.trim()) return null;
    return {
      docType,
      clientId: selectedClient.id,
      periodo: periodo.trim(),
      resumen: resumen.trim(),
      indicadores: indicadores.filter((i) => i.label.trim() && i.valor.trim()),
      analisis: analisis.trim() || undefined,
      hallazgos: hallazgos.trim() || undefined,
      recomendaciones: recomendaciones.trim() || undefined,
    };
  };

  const payload = buildPayload();
  const isValid = payload !== null;

  const handlePreview = async () => {
    if (!payload) {
      toast.error("Complete los campos requeridos antes de previsualizar.");
      return;
    }
    try {
      const html = await preview.mutateAsync(payload);
      setPreviewHtml(html);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleGenerate = async () => {
    if (!payload) {
      toast.error("Complete los campos requeridos antes de generar.");
      return;
    }
    try {
      const result = await generate.mutateAsync(payload);
      await downloadDocumentBytesGlobal(result.document.id);
      toast.success(`${DOC_TYPE_LABEL[docType]} ${result.numeroLabel} generado(a) y guardado(a) en el expediente del cliente.`);
      setPreviewHtml(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Generador de Documentos</h1>
        <p className="text-sm text-gray-500">Cuentas de cobro, facturas, expedientes jurídicos e informes membreteados, listos para PDF.</p>
      </div>

      <div className="card p-4">
        <label className="block text-sm font-medium text-gray-700">Tipo de documento</label>
        <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {TYPE_OPTIONS.map(({ type, icon: Icon, hint }) => (
            <button
              key={type}
              type="button"
              className={`flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors ${
                docType === type ? "border-brand-500 bg-brand-50 ring-1 ring-brand-500" : "border-gray-200 hover:border-brand-300"
              }`}
              onClick={() => setDocType(type)}
            >
              <Icon size={18} className={docType === type ? "text-brand-600" : "text-gray-400"} />
              <span className="text-sm font-medium text-gray-900">{DOC_TYPE_LABEL[type]}</span>
              <span className="text-xs text-gray-500">{hint}</span>
            </button>
          ))}
        </div>
      </div>

      {needsClient && (
        <div className="card p-4">
          <label className="block text-sm font-medium text-gray-700">Cliente</label>
          {selectedClient ? (
            <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
              <span>{selectedClient.name}</span>
              <button type="button" className="text-xs text-gray-400 hover:text-red-600" onClick={() => setSelectedClient(null)}>
                Quitar
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                className="input-field mt-1"
                placeholder="Buscar cliente..."
                value={clientSearch}
                onChange={(e) => setClientSearch(e.target.value)}
              />
              {clientSearch && clientResults?.data && clientResults.data.length > 0 && (
                <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                  {clientResults.data.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                      onClick={() => {
                        setSelectedClient({ id: c.id, name: displayName(c) });
                        setClientSearch("");
                      }}
                    >
                      {displayName(c)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {docType === "CUENTA_COBRO" && (
        <div className="card space-y-3 p-4">
          <div className="flex gap-2">
            <button
              type="button"
              className={`btn-secondary ${cuentaMode === "invoice" ? "ring-1 ring-brand-500" : ""}`}
              onClick={() => setCuentaMode("invoice")}
            >
              Desde una factura
            </button>
            <button
              type="button"
              className={`btn-secondary ${cuentaMode === "manual" ? "ring-1 ring-brand-500" : ""}`}
              onClick={() => setCuentaMode("manual")}
            >
              Manual
            </button>
          </div>

          {cuentaMode === "invoice" ? (
            <div>
              <label className="block text-sm font-medium text-gray-700">Factura con saldo pendiente</label>
              {!selectedClient ? (
                <p className="mt-1 text-xs text-gray-400">Seleccione un cliente para ver sus facturas con saldo.</p>
              ) : invoiceOptions.length === 0 ? (
                <p className="mt-1 text-xs text-gray-400">Este cliente no tiene facturas con saldo pendiente.</p>
              ) : (
                <select className="input-field mt-1" value={selectedInvoiceId} onChange={(e) => setSelectedInvoiceId(e.target.value)}>
                  <option value="">Seleccione una factura...</option>
                  {invoiceOptions.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      N.° {inv.invoiceNumber} — {INVOICE_STATUS_LABEL[inv.status]} — saldo {currency.format(invoiceBalance(inv))}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Concepto</label>
                <textarea className="input-field mt-1" rows={3} value={concepto} onChange={(e) => setConcepto(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Valor</label>
                <input type="number" min="0" className="input-field mt-1" value={valor} onChange={(e) => setValor(e.target.value)} />
              </div>
            </>
          )}
        </div>
      )}

      {docType === "FACTURA" && (
        <div className="card p-4">
          <label className="block text-sm font-medium text-gray-700">Factura</label>
          {!selectedClient ? (
            <p className="mt-1 text-xs text-gray-400">Seleccione un cliente para ver sus facturas.</p>
          ) : invoiceOptions.length === 0 ? (
            <p className="mt-1 text-xs text-gray-400">Este cliente no tiene facturas emitidas.</p>
          ) : (
            <select className="input-field mt-1" value={selectedInvoiceId} onChange={(e) => setSelectedInvoiceId(e.target.value)}>
              <option value="">Seleccione una factura...</option>
              {invoiceOptions.map((inv) => (
                <option key={inv.id} value={inv.id}>
                  N.° {inv.invoiceNumber} — {INVOICE_STATUS_LABEL[inv.status]} — {currency.format(Number(inv.total))}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {docType === "JURIDICO" && (
        <div className="card space-y-3 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Caso jurídico</label>
            {!selectedClient ? (
              <p className="mt-1 text-xs text-gray-400">Seleccione un cliente para ver sus casos.</p>
            ) : caseOptions.length === 0 ? (
              <p className="mt-1 text-xs text-gray-400">Este cliente no tiene casos jurídicos registrados.</p>
            ) : (
              <select className="input-field mt-1" value={selectedCaseId} onChange={(e) => setSelectedCaseId(e.target.value)}>
                <option value="">Seleccione un caso...</option>
                {caseOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title} {c.caseNumber ? `(${c.caseNumber})` : ""} — {LEGAL_CASE_STATUS_LABEL[c.status]}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Última actuación</label>
            <textarea className="input-field mt-1" rows={3} value={ultimaActuacion} onChange={(e) => setUltimaActuacion(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Próxima actuación (opcional)</label>
            <textarea className="input-field mt-1" rows={2} value={proximaActuacion} onChange={(e) => setProximaActuacion(e.target.value)} />
          </div>
        </div>
      )}

      {docType === "PODER_ESPECIAL" && (
        <div className="card space-y-3 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Caso jurídico</label>
            {!selectedClient ? (
              <p className="mt-1 text-xs text-gray-400">Seleccione un cliente para ver sus casos.</p>
            ) : caseOptions.length === 0 ? (
              <p className="mt-1 text-xs text-gray-400">Este cliente no tiene casos jurídicos registrados.</p>
            ) : (
              <select className="input-field mt-1" value={selectedCaseId} onChange={(e) => setSelectedCaseId(e.target.value)}>
                <option value="">Seleccione un caso...</option>
                {caseOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title} {c.caseNumber ? `(${c.caseNumber})` : ""} — {LEGAL_CASE_STATUS_LABEL[c.status]}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nombre del apoderado</label>
              <input className="input-field mt-1" value={apoderadoNombre} onChange={(e) => setApoderadoNombre(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Tarjeta profesional (opcional)</label>
              <input className="input-field mt-1" value={apoderadoTarjetaProfesional} onChange={(e) => setApoderadoTarjetaProfesional(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Facultades conferidas</label>
            <textarea className="input-field mt-1" rows={4} value={facultades} onChange={(e) => setFacultades(e.target.value)} />
          </div>
        </div>
      )}

      {docType === "ACTA_CONCILIACION" && (
        <div className="card space-y-3 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Caso jurídico relacionado (opcional)</label>
            {!selectedClient ? (
              <p className="mt-1 text-xs text-gray-400">Seleccione un cliente para ver sus casos.</p>
            ) : caseOptions.length === 0 ? (
              <p className="mt-1 text-xs text-gray-400">Este cliente no tiene casos jurídicos registrados.</p>
            ) : (
              <select className="input-field mt-1" value={selectedCaseId} onChange={(e) => setSelectedCaseId(e.target.value)}>
                <option value="">Sin caso relacionado</option>
                {caseOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title} {c.caseNumber ? `(${c.caseNumber})` : ""} — {LEGAL_CASE_STATUS_LABEL[c.status]}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Lugar</label>
            <input className="input-field mt-1" placeholder="Ej: Oficinas de la firma, Bogotá D.C." value={lugar} onChange={(e) => setLugar(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Hechos (opcional)</label>
            <textarea className="input-field mt-1" rows={3} value={hechos} onChange={(e) => setHechos(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Acuerdos</label>
            <textarea className="input-field mt-1" rows={4} value={acuerdos} onChange={(e) => setAcuerdos(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Compromisos y plazos (opcional)</label>
            <textarea className="input-field mt-1" rows={3} value={compromisos} onChange={(e) => setCompromisos(e.target.value)} />
          </div>
        </div>
      )}

      {docType === "REQUERIMIENTO_PAGO" && (
        <div className="card space-y-3 p-4">
          <div className="flex gap-2">
            <button
              type="button"
              className={`btn-secondary ${requerimientoMode === "invoice" ? "ring-1 ring-brand-500" : ""}`}
              onClick={() => setRequerimientoMode("invoice")}
            >
              Desde una factura
            </button>
            <button
              type="button"
              className={`btn-secondary ${requerimientoMode === "manual" ? "ring-1 ring-brand-500" : ""}`}
              onClick={() => setRequerimientoMode("manual")}
            >
              Manual
            </button>
          </div>

          {requerimientoMode === "invoice" ? (
            <div>
              <label className="block text-sm font-medium text-gray-700">Factura con saldo pendiente</label>
              {!selectedClient ? (
                <p className="mt-1 text-xs text-gray-400">Seleccione un cliente para ver sus facturas con saldo.</p>
              ) : invoiceOptions.length === 0 ? (
                <p className="mt-1 text-xs text-gray-400">Este cliente no tiene facturas con saldo pendiente.</p>
              ) : (
                <select className="input-field mt-1" value={selectedInvoiceId} onChange={(e) => setSelectedInvoiceId(e.target.value)}>
                  <option value="">Seleccione una factura...</option>
                  {invoiceOptions.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      N.° {inv.invoiceNumber} — {INVOICE_STATUS_LABEL[inv.status]} — saldo {currency.format(invoiceBalance(inv))}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Concepto</label>
                <textarea className="input-field mt-1" rows={3} value={reqConcepto} onChange={(e) => setReqConcepto(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Valor</label>
                <input type="number" min="0" className="input-field mt-1" value={reqValor} onChange={(e) => setReqValor(e.target.value)} />
              </div>
            </>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Plazo para pagar (días hábiles)</label>
              <input type="number" min="1" className="input-field mt-1" value={plazoDias} onChange={(e) => setPlazoDias(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Advertencia (opcional — se usa un texto estándar si se deja vacío)</label>
            <textarea className="input-field mt-1" rows={2} value={advertencia} onChange={(e) => setAdvertencia(e.target.value)} />
          </div>
        </div>
      )}

      {docType === "CONTRATO_SERVICIOS" && (
        <div className="card space-y-3 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Objeto del contrato</label>
            <textarea className="input-field mt-1" rows={3} value={objeto} onChange={(e) => setObjeto(e.target.value)} />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Valor de los honorarios</label>
              <input type="number" min="0" className="input-field mt-1" value={valorHonorarios} onChange={(e) => setValorHonorarios(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Vigencia</label>
              <input
                className="input-field mt-1"
                placeholder="Ej: 12 meses, prorrogable automáticamente"
                value={vigencia}
                onChange={(e) => setVigencia(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Forma de pago</label>
            <textarea className="input-field mt-1" rows={2} value={formaPago} onChange={(e) => setFormaPago(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Cláusulas adicionales (opcional)</label>
            <textarea className="input-field mt-1" rows={3} value={clausulasAdicionales} onChange={(e) => setClausulasAdicionales(e.target.value)} />
          </div>
        </div>
      )}

      {docType === "CERTIFICACION_LABORAL" && (
        <div className="card space-y-3 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Empleado</label>
            {!selectedClient ? (
              <p className="mt-1 text-xs text-gray-400">Seleccione un cliente para ver sus empleados vinculados (nómina tercerizada).</p>
            ) : employeeOptions.length === 0 ? (
              <p className="mt-1 text-xs text-gray-400">Este cliente no tiene empleados registrados.</p>
            ) : (
              <select className="input-field mt-1" value={selectedEmployeeId} onChange={(e) => setSelectedEmployeeId(e.target.value)}>
                <option value="">Seleccione un empleado...</option>
                {employeeOptions.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.fullName} — {emp.position || "Sin cargo"} — {EMPLOYEE_STATUS_LABEL[emp.status]}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Dirigido a (opcional)</label>
              <input className="input-field mt-1" placeholder="A quien interese" value={dirigidoA} onChange={(e) => setDirigidoA(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Propósito (opcional)</label>
              <input
                className="input-field mt-1"
                placeholder="Ej: trámite de visa, crédito hipotecario"
                value={proposito}
                onChange={(e) => setProposito(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {docType === "INFORME" && (
        <div className="card space-y-3 p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Periodo</label>
              <input
                className="input-field mt-1"
                placeholder="Ej: Primer trimestre de 2026"
                value={periodo}
                onChange={(e) => setPeriodo(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Resumen ejecutivo</label>
            <textarea className="input-field mt-1" rows={3} value={resumen} onChange={(e) => setResumen(e.target.value)} />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">Indicadores</label>
              {indicadores.length < 6 && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
                  onClick={() => setIndicadores((prev) => [...prev, { label: "", valor: "" }])}
                >
                  <Plus size={12} /> Agregar indicador
                </button>
              )}
            </div>
            <div className="mt-2 space-y-2">
              {indicadores.map((ind, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    className="input-field flex-1 text-xs"
                    placeholder="Nombre (ej: Ingresos)"
                    value={ind.label}
                    onChange={(e) => setIndicadores((prev) => prev.map((i, idx) => (idx === index ? { ...i, label: e.target.value } : i)))}
                  />
                  <input
                    className="input-field w-40 text-xs"
                    placeholder="Valor"
                    value={ind.valor}
                    onChange={(e) => setIndicadores((prev) => prev.map((i, idx) => (idx === index ? { ...i, valor: e.target.value } : i)))}
                  />
                  {indicadores.length > 1 && (
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-600"
                      onClick={() => setIndicadores((prev) => prev.filter((_, idx) => idx !== index))}
                      aria-label="Quitar indicador"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Análisis (opcional)</label>
            <textarea className="input-field mt-1" rows={3} value={analisis} onChange={(e) => setAnalisis(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Hallazgos (opcional)</label>
            <textarea className="input-field mt-1" rows={3} value={hallazgos} onChange={(e) => setHallazgos(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Recomendaciones (opcional)</label>
            <textarea className="input-field mt-1" rows={3} value={recomendaciones} onChange={(e) => setRecomendaciones(e.target.value)} />
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" disabled={!isValid || preview.isPending} onClick={handlePreview}>
          <Eye size={16} className="mr-1.5" /> {preview.isPending ? "Cargando..." : "Vista previa"}
        </button>
        <button type="button" className="btn-primary" disabled={!isValid || generate.isPending} onClick={handleGenerate}>
          <Download size={16} className="mr-1.5" /> {generate.isPending ? "Generando..." : "Generar PDF"}
        </button>
      </div>

      {previewHtml && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="flex h-[90vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h2 className="text-sm font-semibold text-gray-900">Vista previa — {DOC_TYPE_LABEL[docType]}</h2>
              <button type="button" className="text-gray-400 hover:text-gray-600" onClick={() => setPreviewHtml(null)} aria-label="Cerrar">
                <X size={18} />
              </button>
            </div>
            <iframe title="Vista previa del documento" srcDoc={previewHtml} className="flex-1 w-full rounded-b-lg" />
          </div>
        </div>
      )}
    </div>
  );
}
